import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  ShoppingBag,
  ShieldCheck,
  ArrowRight,
  Lock,
  Banknote,
  Bitcoin,
  Coins,
  CreditCard,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatAUD } from "../mock";
import { useToast } from "../hooks/use-toast";
import { createOrder, getExchangeRates, createStripeCheckout } from "../api";

const PAYMENT_METHODS = [
  {
    id: "xmr",
    icon: Coins,
    name: "Monero (XMR)",
    tagline: "Untraceable by design. Recommended.",
    crypto: true,
  },
  {
    id: "ltc",
    icon: Bitcoin,
    name: "Litecoin · MWEB",
    tagline: "Fast confirmations. Private via MimbleWimble.",
    crypto: true,
  },
  {
    id: "card",
    icon: CreditCard,
    name: "Card (Stripe)",
    tagline: "Visa, Mastercard, Amex. Instant confirmation.",
    crypto: false,
    stripe: true,
  },
  {
    id: "bank",
    icon: Banknote,
    name: "Bank transfer",
    tagline: "Australian PayID / OSKO. Instructions on order confirmation.",
    crypto: false,
  },
];

const Cart = () => {
  const { items, removeItem, total, clear } = useCart();
  const { toast } = useToast();
  const nav = useNavigate();

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("xmr");
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getExchangeRates()
      .then(setRates)
      .catch(() => setRates(null));
  }, []);

  const discountPct = rates?.discount_percent ?? 8;
  const method = PAYMENT_METHODS.find((m) => m.id === paymentMethod);
  const discount = method?.crypto ? Math.round((total * discountPct) / 100) : 0;
  const finalTotal = total - discount;

  const cryptoPreview = (() => {
    if (!rates || !method?.crypto || finalTotal <= 0) return null;
    if (method.id === "xmr") return { code: "XMR", amount: (finalTotal / rates.xmr_aud).toFixed(6) };
    if (method.id === "ltc") return { code: "LTC", amount: (finalTotal / rates.ltc_aud).toFixed(6) };
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        payment_method: paymentMethod,
        items: items.map((it) => ({
          product_id: it.productId,
          tier_id: it.tierId || null,
          variant: it.variant || it.storage,
          storage: it.storage,
          qty: it.qty,
        })),
      };
      const order = await createOrder(payload);
      if (paymentMethod === "card") {
        // Redirect to Stripe Checkout. Cart stays intact until success page confirms.
        const { checkout_url } = await createStripeCheckout({
          order_number: order.order_number,
          origin_url: window.location.origin,
        });
        // Save order number so /payment/success can route back if session param missing
        try { localStorage.setItem("pending_order", order.order_number); } catch {}
        window.location.href = checkout_url;
        return;
      }
      clear();
      toast({ title: "Order placed", description: `Reference ${order.order_number}` });
      nav(`/order/${order.order_number}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast({ title: "Checkout failed", description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Keep shopping
        </Link>
        <h1 className="mt-4 font-headline text-4xl md:text-6xl font-bold text-gradient-purple leading-[0.95]">
          Your bag.
        </h1>

        {items.length === 0 ? (
          <div className="mt-16 card-glass rounded-3xl p-14 text-center">
            <ShoppingBag className="h-10 w-10 text-purple-300 mx-auto" />
            <div className="mt-4 font-display text-2xl">Nothing here yet.</div>
            <p className="mt-2 text-white/60">Pick a device and a tier to get started.</p>
            <Link to="/shop" className="btn-primary mt-8 inline-flex">
              Browse devices <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-4">
              {items.map((it, idx) => (
                <div key={idx} className="card-glass rounded-2xl p-5 flex items-center gap-4">
                  <div className="h-20 w-20 rounded-xl bg-cover bg-center flex-shrink-0" style={{ backgroundImage: `url(${it.image})` }} />
                  <div className="flex-1">
                    <div className="font-display font-semibold text-white">{it.productName}</div>
                    <div className="text-xs text-purple-300 mt-1 uppercase tracking-[0.2em]">
                      {it.tierName ? `${it.tierName} tier · ${it.variant || it.storage}` : (it.variant || it.storage)}
                    </div>
                    <div className="text-xs text-white/40 mt-1">Qty {it.qty}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg text-white">{formatAUD(it.price * it.qty)}</div>
                    <button onClick={() => removeItem(idx)} className="mt-2 inline-flex items-center gap-1 text-xs text-white/50 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={clear} className="text-xs text-white/40 hover:text-white/70">Clear bag</button>
            </div>

            <aside className="card-glass rounded-2xl p-6 h-fit sticky top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">Summary</div>
              <div className="mt-4 flex justify-between text-sm text-white/70">
                <span>Subtotal</span>
                <span>{formatAUD(total)}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-white/70">
                <span>Discreet shipping</span>
                <span className="text-purple-300">Included</span>
              </div>
              <div className="mt-4 flex items-baseline justify-between text-sm text-purple-300">
                <span>Pay in crypto → save {discountPct}%</span>
                <span className="text-xs text-white/40">applied at checkout</span>
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 flex justify-between">
                <span className="font-display">Total</span>
                <span className="font-display text-2xl font-bold text-white">{formatAUD(total)}</span>
              </div>
              <button onClick={() => setCheckoutOpen(true)} className="btn-primary w-full mt-6 justify-center">
                Checkout <ArrowRight className="h-4 w-4" />
              </button>
              <div className="mt-4 flex items-center gap-2 text-xs text-white/40">
                <ShieldCheck className="h-3.5 w-3.5 text-purple-400" />
                <span>Tamper-evident packaging on every order.</span>
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* Checkout drawer */}
      {checkoutOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-3xl w-full max-w-2xl p-6 md:p-8 my-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="chip"><Lock className="h-3 w-3" /> Discreet checkout</div>
                <h2 className="mt-3 font-headline text-3xl font-bold text-gradient-purple">Your details.</h2>
              </div>
              <button onClick={() => setCheckoutOpen(false)} className="h-9 w-9 rounded-full border border-white/10 text-white/60 hover:text-white">×</button>
            </div>
            <p className="mt-2 text-sm text-white/50">
              We hold this data for shipping only and delete it 30 days after delivery.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="co-input" placeholder="For shipping paperwork." />
                </Field>
                <Field label="Email" required>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="co-input" placeholder="Confirmation goes here." />
                </Field>
              </div>
              <Field label="Phone (optional)">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="co-input" placeholder="For courier only." />
              </Field>
              <Field label="Shipping address" required>
                <textarea required rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="co-input resize-none" placeholder="Street, suburb, state, postcode." />
              </Field>
              <Field label="Notes (optional)">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="co-input resize-none" placeholder="Delivery instructions, alias, drop-off spot…" />
              </Field>

              {/* Payment methods */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-2">Payment method</div>
                <div className="grid gap-2">
                  {PAYMENT_METHODS.map((m) => {
                    const Ico = m.icon;
                    const active = paymentMethod === m.id;
                    return (
                      <button type="button" key={m.id} onClick={() => setPaymentMethod(m.id)} className={`text-left rounded-2xl p-3.5 border transition-all flex items-center justify-between gap-3 ${active ? "border-purple-500/60 bg-purple-500/5" : "border-white/10 bg-white/[0.02] hover:border-purple-500/30"}`}>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                            <Ico className="h-4 w-4 text-purple-300" />
                          </div>
                          <div>
                            <div className="font-display font-semibold text-white text-sm flex items-center gap-2">
                              {m.name}
                              {m.crypto && (
                                <span className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border border-purple-500/40 text-purple-300">
                                  −{discountPct}%
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-white/50">{m.tagline}</div>
                          </div>
                        </div>
                        {active && <div className="h-2 w-2 rounded-full bg-purple-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Totals preview */}
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 text-sm">
                <Row label="Subtotal" value={formatAUD(total)} muted />
                {method?.crypto && (
                  <Row label={`Crypto discount (${discountPct}%)`} value={`− ${formatAUD(discount)}`} highlight />
                )}
                <div className="h-px bg-white/10 my-2" />
                <Row label="Total" value={formatAUD(finalTotal)} bold />
                {cryptoPreview && (
                  <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-xs">
                    <span className="text-white/50">Approx.</span>
                    <span className="font-mono text-purple-300">{cryptoPreview.amount} {cryptoPreview.code}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">You pay</div>
                  <div className="font-display text-2xl font-bold text-white">{formatAUD(finalTotal)}</div>
                </div>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="place-order-btn">
                  {submitting
                    ? (method?.stripe ? "Redirecting to Stripe…" : "Placing…")
                    : method?.stripe
                      ? (<>Pay with card <ArrowRight className="h-4 w-4" /></>)
                      : (<>Place order <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </div>

              <p className="text-[11px] text-white/40 leading-relaxed">
                {method?.crypto
                  ? `Wallet address and exact ${method.name.split(" ")[0]} amount appear on the next screen. Send from any wallet you like — we verify on-chain and confirm within 24 hours.`
                  : method?.stripe
                    ? "You'll be redirected to Stripe's secure checkout. Test cards accepted in sandbox mode — use 4242 4242 4242 4242 with any future expiry and any CVC."
                    : "Bank transfer details are shown on the next screen. Reference your order number when transferring."}
              </p>
            </form>
          </div>

          <style>{`
            .co-input {
              width: 100%;
              background: rgba(255,255,255,0.03);
              border: 1px solid rgba(255,255,255,0.08);
              border-radius: 12px;
              padding: 11px 13px;
              color: #fff;
              font-size: 14px;
              outline: none;
              transition: border-color 200ms ease, background 200ms ease;
            }
            .co-input:focus {
              border-color: rgba(168,85,247,0.6);
              background: rgba(168,85,247,0.05);
            }
          `}</style>
        </div>
      )}
    </main>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-1.5">
      {label} {required && <span className="text-purple-400">*</span>}
    </div>
    {children}
  </label>
);

const Row = ({ label, value, muted, bold, highlight }) => (
  <div className="flex justify-between py-1">
    <span className={muted ? "text-white/50" : highlight ? "text-purple-300" : "text-white/80"}>{label}</span>
    <span className={`${bold ? "font-display font-bold text-white text-base" : ""} ${highlight ? "text-purple-300" : "text-white/90"}`}>{value}</span>
  </div>
);

export default Cart;
